import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Users, BookOpen, IndianRupee, PlayCircle, Download,
  Layers, ChevronUp, ChevronDown, BarChart3, Calendar,
} from "lucide-react";
import { PageHeader, SearchInput, FilterSelect, StatusBadge } from "../../ui";
import apiClient from "../../../utils/apiClient";
import "./sa-analytics.scss";

const getMonthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const getToday = () => new Date().toISOString().slice(0, 10);

const SAInstitutesOverview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ institutes: [], summary: {}, total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState(getMonthStart());
  const [dateTo, setDateTo] = useState(getToday());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (sortField) params.sort = `${sortField}:${sortDir}`;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const res = await apiClient.get("/sa/analytics/overview", { params });
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sortField, sortDir, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounced search
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const handleExport = async () => {
    try {
      let qs = "";
      if (dateFrom) qs += `&from=${dateFrom}`;
      if (dateTo) qs += `&to=${dateTo}`;
      const res = await apiClient.get(`/sa/analytics/overview/export?${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `institutes-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent
    }
  };

  const fmt = (v) => (v ?? 0).toLocaleString("en-IN");
  const fmtCurrency = (v, currency = "INR") => {
    const sym = currency === "INR" ? "₹" : "$";
    if (v >= 100000) return `${sym}${(v / 100000).toFixed(1)}L`;
    if (v >= 1000) return `${sym}${(v / 1000).toFixed(1)}K`;
    return `${sym}${v}`;
  };

  const { summary } = data;

  return (
    <div className="sa-analytics">
      <PageHeader
        title="Institute Analytics"
        description="Billing & revenue overview across all institutes"
        icon={BarChart3}
        actions={
          <button className="sa-export-btn" onClick={handleExport}>
            <Download size={14} /> Export CSV
          </button>
        }
      />

      {/* Summary KPIs */}
      <div className="sa-grid-4">
        <div className="sa-kpi-card">
          <div className="sa-kpi-inner">
            <div className="sa-kpi-info">
              <span className="sa-kpi-label">Total Institutes</span>
              <span className="sa-kpi-value">{fmt(summary.totalInstitutes)}</span>
            </div>
            <div className="sa-kpi-icon blue"><Building2 size={20} /></div>
          </div>
        </div>
        <div className="sa-kpi-card">
          <div className="sa-kpi-inner">
            <div className="sa-kpi-info">
              <span className="sa-kpi-label">Total Students</span>
              <span className="sa-kpi-value">{fmt(summary.totalStudents)}</span>
            </div>
            <div className="sa-kpi-icon violet"><Users size={20} /></div>
          </div>
        </div>
        <div className="sa-kpi-card">
          <div className="sa-kpi-inner">
            <div className="sa-kpi-info">
              <span className="sa-kpi-label">Total Income</span>
              <span className="sa-kpi-value">{fmtCurrency(summary.totalIncome)}</span>
            </div>
            <div className="sa-kpi-icon emerald"><IndianRupee size={20} /></div>
          </div>
        </div>
        <div className="sa-kpi-card">
          <div className="sa-kpi-inner">
            <div className="sa-kpi-info">
              <span className="sa-kpi-label">Platform Commission</span>
              <span className="sa-kpi-value">{fmtCurrency(summary.totalCommission)}</span>
            </div>
            <div className="sa-kpi-icon amber"><IndianRupee size={20} /></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sa-analytics-filters">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search institutes..."
        />
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { label: "All Status", value: "all" },
            { label: "Active", value: "active" },
            { label: "Trial", value: "trial" },
            { label: "Suspended", value: "suspended" },
            { label: "Inactive", value: "inactive" },
          ]}
        />
        <div className="sa-date-range">
          <Calendar size={14} />
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="sa-date-input" />
          <span className="sa-date-sep">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="sa-date-input" />
        </div>
      </div>

      {/* Table */}
      <div className="sa-card">
        <div className="sa-card-body sa-card-body--table">
          {loading ? (
            <div className="sa-empty">Loading...</div>
          ) : data.institutes.length === 0 ? (
            <div className="sa-empty">No institutes found</div>
          ) : (
            <div className="sa-analytics-table-wrap">
              <table className="sa-mini-table sa-analytics-table">
                <thead>
                  <tr>
                    <th>Institute</th>
                    <th className="sa-th-sort" onClick={() => handleSort("totalStudents")}>
                      <Users size={12} /> Students <SortIcon field="totalStudents" />
                    </th>
                    <th className="sa-th-sort" onClick={() => handleSort("batchCourses")}>
                      <BookOpen size={12} /> Courses <SortIcon field="batchCourses" />
                    </th>
                    <th className="sa-th-sort" onClick={() => handleSort("totalBatches")}>
                      <Layers size={12} /> Batches <SortIcon field="totalBatches" />
                    </th>
                    <th className="sa-th-sort" onClick={() => handleSort("selfPacedCourses")}>
                      <PlayCircle size={12} /> Self-Paced <SortIcon field="selfPacedCourses" />
                    </th>
                    <th className="sa-th-sort" onClick={() => handleSort("totalIncome")}>
                      <IndianRupee size={12} /> Income <SortIcon field="totalIncome" />
                    </th>
                    <th className="sa-th-sort" onClick={() => handleSort("platformCommission")}>
                      Commission <SortIcon field="platformCommission" />
                    </th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.institutes.map((inst) => (
                    <tr
                      key={inst.instituteId}
                      className="sa-mini-table__row--clickable"
                      onClick={() => navigate(`/sa/analytics/${inst.instituteId}`)}
                    >
                      <td>
                        <div className="sa-inst-name">
                          <div className="sa-inst-avatar">{inst.name?.[0] || "?"}</div>
                          <div>
                            <div className="sa-inst-name__text">{inst.name}</div>
                            <div className="sa-inst-name__email">{inst.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{fmt(inst.totalStudents)}</td>
                      <td>{fmt(inst.batchCourses)}</td>
                      <td>{fmt(inst.totalBatches)}</td>
                      <td>
                        {fmt(inst.selfPacedCourses)}
                        {inst.selfPacedEnrollments > 0 && (
                          <span className="sa-sub-count">{fmt(inst.selfPacedEnrollments)} enrolled</span>
                        )}
                      </td>
                      <td className="sa-income-cell">
                        {fmtCurrency(inst.totalIncome, inst.currency)}
                      </td>
                      <td className="sa-commission-cell">
                        {inst.platformCommission > 0
                          ? fmtCurrency(inst.platformCommission, inst.currency)
                          : "—"}
                      </td>
                      <td>
                        <StatusBadge status={inst.status || "active"}>
                          {inst.status || "active"}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="sa-pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span>Page {data.page} of {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default SAInstitutesOverview;
